import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import { FixedSizeGrid as Grid } from "react-window";
import {
  MenuItem,
  Select,
  TextField,
  Skeleton,
  Tabs,
  Tab,
} from "@mui/material";
import { toggleImageSelection } from "../../../redux/Slice/mediaSlice.js";
import { useDispatch, useSelector } from "react-redux";
import { getMediaList } from "services/apiService";
import ImageUpload from "common/ImageUpload";
import config from "config";

const ImageGallery = ({
  isOpen,
  onRequestClose,
  isMulti,
  keyName,
  handleBlur,
}) => {
  const dispatch = useDispatch();
  const AppUrl = config.REACT_APP_IMAGE_URL;

  const selectedImagesByKey = useSelector(
    (state: any) => state.media.selectedImagesByKey
  );
  const singleImageSelections = useSelector(
    (state: any) => state.media.singleImageSelections
  );

  const selectedImage = selectedImagesByKey
    ? selectedImagesByKey[keyName]
    : undefined;
  const selectedList = selectedImage
    ? selectedImage.split(",").filter(Boolean)
    : [];
  const singleSelect = singleImageSelections
    ? singleImageSelections[keyName]
    : "";
  const [tabIndex, setTabIndex] = useState(0);
  const [imageGallery, setImageGallery] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchAllImages();
    }
  }, [isOpen]);

  const fetchAllImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMediaList();
      setImageGallery(response?.data || []);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
    setLoading(false);
  }, []);

  const handleImageToggle = (mediaUrl) => {
    dispatch(toggleImageSelection({ keyName, mediaUrl, isMulti }));
    if (!isMulti) onRequestClose();
  };

  const handleConfirm = () => onRequestClose();

  const handleTabs = () => {
    setTabIndex(0);
    fetchAllImages();
  };

  const filteredImages = useMemo(() => {
    return imageGallery
      .filter((img) => {
        const matchesSearch = img
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        const matchesFilter = filterType === "all" || img.endsWith(filterType);
        return matchesSearch && matchesFilter;
      })
      .slice(0, 100);
  }, [imageGallery, searchQuery, filterType]);

  const cellRenderer = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * 5 + columnIndex;
    const mediaUrl = filteredImages[index];

    if (!mediaUrl) return null;

    const isChecked = isMulti
      ? selectedList.includes(mediaUrl)
      : singleSelect === mediaUrl;

    return (
      <div style={{ ...style, padding: "10px" }} key={index}>
        <div
          style={{
            width: "100%",
            border: "1px solid #ddd",
            padding: "10px",
            borderRadius: "8px",
            position: "relative",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
          }}
        >
          {loading ? (
            <Skeleton variant="rectangular" width="100%" height={150} />
          ) : (
            <img
              src={AppUrl + mediaUrl}
              alt="media"
              style={{
                width: "100%",
                height: "150px",
                objectFit: "cover",
                borderRadius: "5px",
              }}
            />
          )}
          <p style={{ fontSize: "12px", marginTop: "8px" }}>
            {mediaUrl.split("/").pop().length > 20
              ? mediaUrl.split("/").pop().slice(0, 12) + "..."
              : mediaUrl.split("/").pop()}
          </p>
          <Checkbox
            checked={isChecked}
            onChange={() => handleImageToggle(mediaUrl)}
            style={{ position: "absolute", top: "5px", right: "5px" }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal open={isOpen} onClose={onRequestClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: 24,
            p: 3,
            maxWidth: "1200px",
            width: "95%",
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={(e, newValue) => setTabIndex(newValue)}
            centered
          >
            <Tab label=" Gallery" />
            <Tab label="Upload Images" />
          </Tabs>
          {tabIndex === 0 && (
            <Box>
              {/* Filters */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  sx={{ width: 150, fontSize: "14px" }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value=".jpg">JPG</MenuItem>
                  <MenuItem value=".jpeg">JPEG</MenuItem>
                  <MenuItem value=".png">PNG</MenuItem>
                  <MenuItem value=".gif">GIF</MenuItem>
                  <MenuItem value=".webp">WEBP</MenuItem>
                  <MenuItem value=".svg">SVG</MenuItem>
                </Select>

                <TextField
                  variant="outlined"
                  placeholder="Search images..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: 250 }}
                />
              </Box>

              {/* Action Buttons */}
              {isMulti && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    mt: 2,
                  }}
                >
                  <Button variant="outlined" onClick={onRequestClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                  >
                    Confirm
                  </Button>
                </Box>
              )}

              {/* Image Grid */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 550,
                }}
              >
                {loading ? (
                  <Skeleton variant="rectangular" width={1000} height={550} />
                ) : filteredImages.length > 0 ? (
                  <Grid
                    columnCount={5}
                    columnWidth={180}
                    height={550}
                    rowCount={Math.ceil(filteredImages.length / 5)}
                    rowHeight={220}
                    width={1000}
                  >
                    {cellRenderer}
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      fontSize: "18px",
                      color: "grey",
                    }}
                  >
                    No data found
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {tabIndex === 1 && <ImageUpload onUploadSuccess={handleTabs} />}
        </Box>
      </Modal>
    </>
  );
};

export default ImageGallery;
